import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api, ApiError } from '../lib/api';

/**
 * MentorContext — provides dashboard data (scope, status, suggestion counts)
 * to all mentor pages. Fetched once on mount; refetch available for consumers.
 *
 * The MentorLayout wraps its children with this provider so that:
 *   - Navigation is scope-filtered without each page fetching separately
 *   - Pages can access applicant display name, link status, etc.
 *
 * Requirements: 1.2, 2.5, 6.5
 */

export interface DashboardData {
  status: 'active' | 'invited' | 'revoked' | 'no_link_found';
  scope: string[];
  applicant_display_name: string | null;
  suggestion_counts: {
    open: number;
    accepted: number;
    dismissed: number;
  };
  message?: string;
}

export interface MentorContextValue {
  dashboard: DashboardData | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const MentorContext = createContext<MentorContextValue | null>(null);

export function MentorProvider({ children }: { children: React.ReactNode }) {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<DashboardData>('/api/mentors/dashboard');
      setDashboard(data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // No mentor link found — represent as a valid state
        setDashboard({
          status: 'no_link_found',
          scope: [],
          applicant_display_name: null,
          suggestion_counts: { open: 0, accepted: 0, dismissed: 0 },
          message: 'No mentoring relationship found.',
        });
      } else {
        setError('Dashboard data could not be loaded.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return (
    <MentorContext.Provider value={{ dashboard, loading, error, refetch: fetchDashboard }}>
      {children}
    </MentorContext.Provider>
  );
}

export function useMentorContext(): MentorContextValue {
  const ctx = useContext(MentorContext);
  if (!ctx) {
    throw new Error('useMentorContext must be used within a MentorProvider');
  }
  return ctx;
}
